/* src/components/hooks/usePlanes.ts */

'use client';

import { useEffect, useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Contratacion {
  id_contratacion: string;
  id_plan: string;
  plan: string;
  descripcion_plan?: string;
  precio_mensual: number;
  es_gratis: boolean;
  fecha_inicio: string;
  fecha_fin?: string | null;
  activo: boolean;
  es_vigente: boolean;
  puede_reactivar: boolean;
  auto_renovacion: boolean;
  dias_restantes?: number | null;
}

export interface Servicio {
  servicio_saas: string;
  descripcion_corta: string | null;
  orden_servicio: number;
  orden_categoria: number;
  categoria: string;
}

export interface PlanVista {
  id_plan: string;
  plan: string;
  orden_plan: number;
  descripcion_plan?: string;
  precio_mensual: number;
  precio_trabajador_adicional: number;
  tiene_promocion: boolean;
  precio_promocion: number | null;
  fecha_inicio_promocion: string | null;
  fecha_fin_promocion: string | null;
  promocion_activa: boolean;
  es_gratis: boolean;
  servicios: Servicio[];
}

export interface MovimientoPlan {
  fecha: string;
  descripcion: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = new Date(iso);
  return (
    d.toLocaleDateString() +
    ' ' +
    d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  );
};

/**
 * Agrupa filas planas de vista_planes_servicios en Plan[] con servicios anidados.
 */
const agruparPlanes = (rows: any[]): PlanVista[] => {
  const map = new Map<string, PlanVista>();

  rows.forEach((row) => {
    if (!map.has(row.id_plan)) {
      map.set(row.id_plan, {
        id_plan: row.id_plan,
        plan: row.plan,
        orden_plan: row.orden_plan,
        descripcion_plan: row.descripcion_plan,
        precio_mensual: row.precio_mensual,
        precio_trabajador_adicional: row.precio_trabajador_adicional ?? 0,
        tiene_promocion: row.tiene_promocion ?? false,
        precio_promocion: row.precio_promocion ?? null,
        fecha_inicio_promocion: row.fecha_inicio_promocion ?? null,
        fecha_fin_promocion: row.fecha_fin_promocion ?? null,
        promocion_activa: row.promocion_activa ?? false,
        es_gratis: row.es_gratis ?? false,
        servicios: [],
      });
    }

    if (row.servicio_saas) {
      const plan = map.get(row.id_plan)!;
      // Evitar duplicados
      const yaExiste = plan.servicios.some(
        (s) => s.servicio_saas === row.servicio_saas
      );
      if (!yaExiste) {
        plan.servicios.push({
          servicio_saas: row.servicio_saas,
          descripcion_corta: row.descripcion_corta ?? null,
          orden_servicio: row.orden_servicio ?? 0,
          orden_categoria: row.orden_categoria ?? 0,
          categoria: row.categoria ?? '',
        });
      }
    }
  });

  // Ordenar planes por orden_plan y servicios internamente
  return Array.from(map.values())
    .sort((a, b) => a.orden_plan - b.orden_plan)
    .map((plan) => ({
      ...plan,
      servicios: plan.servicios.sort(
        (a, b) => a.orden_servicio - b.orden_servicio
      ),
    }));
};

// ─── Hook principal ───────────────────────────────────────────────────────────

export function usePlanes(idEmpresa: string, userId: string) {
  const [planes, setPlanes] = useState<PlanVista[]>([]);
  const [contratacionActual, setContratacionActual] =
    useState<Contratacion | null>(null);
  const [loading, setLoading] = useState(true);
  const [historial, setHistorial] = useState<MovimientoPlan[]>([]);
  const [paginaHistorial, setPaginaHistorial] = useState(1);
  const [hayMas, setHayMas] = useState(false);

  const porPagina = 5;

  // ── Historial ──────────────────────────────────────────────────────────────
  const fetchMovimientos = useCallback(
    async (pagina: number) => {
      const desde = (pagina - 1) * porPagina;
      const hasta = pagina * porPagina - 1;

      const { data, error, count } = await supabase
        .from('vista_movimientos_planes')
        .select('*', { count: 'exact' })
        .eq('id_empresa', idEmpresa)
        .eq('activo', true)
        .order('fecha_movimiento', { ascending: false })
        .range(desde, hasta);

      if (error) {
        Sentry.captureException(error, { extra: { idEmpresa, pagina } });
        toast.error('Error cargando historial');
        return;
      }

      const mov = (data || []).map((m: any) => ({
        fecha: formatDate(m.fecha_movimiento),
        descripcion:
          m.descripcion ??
          `${m.tipo_movimiento.charAt(0).toUpperCase()}${m.tipo_movimiento
            .slice(1)
            .toLowerCase()} del plan ${m.plan}`,
      }));

      if (pagina === 1) setHistorial(mov);
      else setHistorial((prev) => [...prev, ...mov]);

      setHayMas(count ? hasta + 1 < count : false);
    },
    [idEmpresa]
  );

  // ── Planes + contratación vigente ──────────────────────────────────────────
  const fetchPlanAndSubscription = useCallback(async () => {
    setLoading(true);

    const { data: planesVista, error: planesError } = await supabase
      .from('vista_planes_servicios')
      .select('*')
      .order('orden_plan', { ascending: true });

    if (planesError) {
      Sentry.captureException(planesError, { extra: { idEmpresa } });
      toast.error('Error al cargar los datos de suscripción');
      setLoading(false);
      return;
    }

    setPlanes(agruparPlanes(planesVista || []));

    const { data: vigenteData, error: vigenteError } = await supabase.rpc(
      'get_plan_vigente_o_en_periodo',
      { p_id_empresa: idEmpresa }
    );

    if (vigenteError) {
      Sentry.captureException(vigenteError, { extra: { idEmpresa } });
      toast.error('Error al cargar los datos de suscripción');
      setLoading(false);
      return;
    }

    setContratacionActual(vigenteData?.length > 0 ? vigenteData[0] : null);

    await fetchMovimientos(1);
    setPaginaHistorial(1);

    setLoading(false);
  }, [idEmpresa, fetchMovimientos]);

  // ── Contratar / reactivar ──────────────────────────────────────────────────
  const contratarPlan = async (planId: string) => {
    try {
      const ahora = new Date();

      const { error: desactivarError } = await supabase
        .from('planes_contratados')
        .update({
          activo: false,
          auto_renovacion: false,
          usuario_edicion: userId,
        })
        .eq('id_empresa', idEmpresa)
        .eq('activo', true);

      if (desactivarError) throw desactivarError;

      const { data: existente, error: existeError } = await supabase
        .from('planes_contratados')
        .select('id_contratacion')
        .eq('id_empresa', idEmpresa)
        .eq('id_plan', planId)
        .eq('activo', false)
        .gte('fecha_fin', ahora.toISOString())
        .maybeSingle();

      if (existeError) throw existeError;

      if (existente) {
        const { error: reactivarError } = await supabase
          .from('planes_contratados')
          .update({
            activo: true,
            auto_renovacion: true,
            fecha_edicion: ahora.toISOString(),
            usuario_edicion: userId,
          })
          .eq('id_contratacion', existente.id_contratacion);

        if (reactivarError) throw reactivarError;
        toast.success('Plan reactivado correctamente.');
      } else {
        const { error: insertError } = await supabase
          .from('planes_contratados')
          .insert([
            {
              id_plan: planId,
              id_empresa: idEmpresa,
              activo: true,
              auto_renovacion: true,
              usuario_creacion: userId,
            },
          ]);

        if (insertError) throw insertError;
        toast.success('Plan contratado exitosamente.');
      }

      await fetchPlanAndSubscription();
    } catch (err: any) {
      Sentry.captureException(err, { extra: { idEmpresa, planId } });
      if (err?.code === '23505')
        toast.error('Ya existe un plan activo para esta empresa.');
      else toast.error('Error al contratar o reactivar el plan.');
    }
  };

  // ── Cancelar ───────────────────────────────────────────────────────────────
  const cancelarPlan = async (idContratacion: string | null) => {
    if (!idContratacion) return;

    const { error } = await supabase
      .from('planes_contratados')
      .update({
        activo: false,
        auto_renovacion: false,
        usuario_edicion: userId,
      })
      .eq('id_contratacion', idContratacion);

    if (error) {
      Sentry.captureException(error, { extra: { idContratacion, idEmpresa } });
      toast.error('No se pudo cancelar la suscripción');
    } else {
      toast.success('Suscripción cancelada');
      await fetchPlanAndSubscription();
    }
  };

  // ── Auto-renovación ────────────────────────────────────────────────────────
  const toggleAutoRenovacion = async (
    idContratacion: string,
    value: boolean
  ) => {
    const { error } = await supabase
      .from('planes_contratados')
      .update({ auto_renovacion: value, usuario_edicion: userId })
      .eq('id_contratacion', idContratacion);

    if (error) {
      Sentry.captureException(error, { extra: { idContratacion, value } });
      toast.error('No se pudo actualizar la auto-renovación');
    } else {
      toast.success(
        `Renovación automática ${value ? 'activada' : 'desactivada'}`
      );
    }
  };

  // ── Historial paginado ─────────────────────────────────────────────────────
  const verMasHistorial = async () => {
    const nueva = paginaHistorial + 1;
    setPaginaHistorial(nueva);
    await fetchMovimientos(nueva);
  };

  const reiniciarHistorial = async () => {
    setPaginaHistorial(1);
    await fetchMovimientos(1);
  };

  useEffect(() => {
    fetchPlanAndSubscription();
  }, [fetchPlanAndSubscription]);

  return {
    loading,
    planes,
    contratacionActual,
    historial,
    paginaHistorial,
    hayMas,
    contratarPlan,
    cancelarPlan,
    toggleAutoRenovacion,
    verMasHistorial,
    reiniciarHistorial,
    refetch: fetchPlanAndSubscription,
  };
}

// ─── Standalone: catálogo público de planes ───────────────────────────────────

export async function cargarCatalogoPlanes(): Promise<PlanVista[]> {
  const { data, error } = await supabase
    .from('vista_planes_servicios')
    .select('*')
    .order('orden_plan', { ascending: true });

  if (error) {
    Sentry.captureException(error);
    toast.error('Error al cargar el catálogo de planes');
    return [];
  }

  return agruparPlanes(data || []);
}
